import { colors } from '@mui/material'
import type { PaletteColor, PaletteOptions } from '@mui/material/styles'

/**
 * MUI types augmentation to allow color tokens from color ramp 25...900
 */
declare module '@mui/material/styles' {
    interface PaletteColor {
        25?: string
        50?: string
        100?: string
        200?: string
        300?: string
        400?: string
        500?: string
        600?: string
        700?: string
        800?: string
        900?: string
    }

    interface SimplePaletteColorOptions {
        25?: string
        50?: string
        100?: string
        200?: string
        300?: string
        400?: string
        500?: string
        600?: string
        700?: string
        800?: string
        900?: string
    }
}

/**
 * Color Ramps
 */
type ColorRamp = Required<Omit<PaletteColor, 'light' | 'main' | 'dark' | 'contrastText'>>

const GREY: ColorRamp = {
    25: '#FDFDFD',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E9EAEB',
    300: '#D5D7DA',
    400: '#A4A7AE',
    500: '#717680',
    600: '#535862',
    700: '#414651',
    800: '#252B37',
    900: '#101828',
}

const PRIMARY: ColorRamp = {
    25: '#FCFAFF',
    50: '#E8F6FF',
    100: '#D5EEFF',
    200: '#B3DEFF',
    300: '#85C6FF',
    400: '#569FFF',
    500: '#2F77FF',
    600: '#0C49FF',
    700: '#0039ec',
    800: '#0639CD',
    900: '#10389F',
}

const ERROR: ColorRamp = {
    25: '#FFFBFA',
    50: '#FEF3F2',
    100: '#FEE4E2',
    200: '#FECDCA',
    300: '#FDA29B',
    400: '#F97066',
    500: '#F04438',
    600: '#D92D20',
    700: '#B42318',
    800: '#912018',
    900: '#7A271A',
}

const WARNING: ColorRamp = {
    25: '#FFFCF5',
    50: '#FFFAEB',
    100: '#FEF0C7',
    200: '#FEDF89',
    300: '#FEC84B',
    400: '#FDB022',
    500: '#F79009',
    600: '#DC6803',
    700: '#B54708',
    800: '#93370D',
    900: '#7A2E0E',
}

const SUCCESS: ColorRamp = {
    25: '#F6FEF9',
    50: '#ECFDF3',
    100: '#D1FADF',
    200: '#A6F4C5',
    300: '#6CE9A6',
    400: '#32D583',
    500: '#12B76A',
    600: '#039855',
    700: '#027A48',
    800: '#05603A',
    900: '#054F31',
}

/**
 * "Hanging" colors, used for legacy compatibility (with our old design)
 * @deprecated
 */
const AMBER: Partial<PaletteColor> = {
    main: '#F5A119',
}

/**
 * Actual palette overrides
 */
export const palette: PaletteOptions = {
    ...colors,
    grey: {
        ...GREY,
    },
    primary: {
        ...PRIMARY,
        main: PRIMARY[500],
        light: PRIMARY[200],
        dark: PRIMARY[800],
    },
    error: {
        ...ERROR,
        main: ERROR[500],
        light: ERROR[200],
        dark: ERROR[800],
    },
    warning: {
        ...WARNING,
        main: WARNING[500],
        light: WARNING[200],
        dark: WARNING[800],
    },
    success: {
        ...SUCCESS,
        main: SUCCESS[500],
        light: SUCCESS[100],
        dark: SUCCESS[800],
    },
    info: {
        ...PRIMARY,
        main: PRIMARY[500],
        light: PRIMARY[200],
        dark: PRIMARY[800],
    },
    /**
     * Overrides below are the legacy ones,
     * TODO: Should be refined and replaced with the colors from ramp.
     * TODO: the secondary color should be removed.
     */
    secondary: {
        ...AMBER,
        contrastText: '#FFFFFF',
    },
    background: {
        default: '#FFFFFF',
        paper: GREY[50],
    },
    text: {
        primary: '#262830',
        secondary: '#2D2F3699',
        disabled: '#2D2F3640',
    },
    divider: '#D9D9D9',
}

/**
 * The table colors below is a legacy stuff,
 * TODO: We should use colors from the ramp instead e.g. 'grey.200'
 */
type TableColor = {
    headerbackground: string
    tableborder: string
    cellborder: string
}

export const tableColor: TableColor = {
    headerbackground: '#edf4fb',
    tableborder: '#D5D5D5',
    cellborder: '#D9D9D9',
}
